<script setup lang="ts">
import { computed, onMounted, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { AnnouncementDialog } from "~/components/announcement"
import { TavoProvider } from "~/components/tavo"
import { useTavoConfig } from "~/components/tavo/config"
import { Toaster } from "~/components/ui/sonner"
import { useConcept } from "~/composables/concept"
import { useLocation } from "~/composables/locations"
import { BlankLayout, DefaultLayout } from "~/layouts"

const route = useRoute()
const router = useRouter()
const { concept } = useConcept()
const { location } = useLocation()

const skipLinkFocused = ref(false)

const routeBase = computed(() => route.name?.toString().split("-")[0] || "")

const layout = computed(() => {
    if (!routeBase.value) {
        return BlankLayout
    }
    return DefaultLayout
})

const tavoConfig = useTavoConfig()

onMounted(() => {
    const staticRoutes = ["terms-of-use", "privacy-policy", "accessibility-statement"]
    if (!staticRoutes.includes(route.name as string) && !location.value && route.name !== "concept") {
        router.push({ name: "concept", query: route.query, hash: route.hash })
    }
})

watch(location, (value, oldValue) => {
    if (typeof window !== "undefined") {
        const root = window.document.documentElement
        if (oldValue?.name) {
            const oldClassName = oldValue.name.toLowerCase().replace(/\s+/g, "-")
            root.classList.remove(oldClassName)
        }
        if (value?.name) {
            const className = value.name.toLowerCase().replace(/\s+/g, "-")
            root.classList.add(className)
        }
    }
}, { immediate: true })
</script>

<template>
    <a
        href="#main-content"
        class="skip-link"
        :class="{ 'skip-link--visible': skipLinkFocused }"
        tabindex="0"
        @focus="skipLinkFocused = true"
        @blur="skipLinkFocused = false"
    >Skip to main content</a>
    <Component
        :is="layout"
        vaul-drawer-wrapper
        class="bg-background text-foreground"
    >
        <div
            v-for="(announcement, i) in concept.announcements"
            :key="i"
        >
            <AnnouncementDialog
                :id="i.toString()" 
                :announcement="announcement"
                :concept="concept"
            />
        </div>
        <RouterView />
    </Component>
    <TavoProvider v-if="tavoConfig" />
    <Toaster
        position="bottom-center"
        rich-colors
        aria-live="assertive" 
        aria-atomic="true"
    />
</template>

<style scoped>
/* ACCESSIBILITY: Always fixed at correct position; hidden via transform so
   VoiceOver doesn't try to scroll to an off-screen coordinate */
.skip-link {
    position: fixed;
    top: calc(env(safe-area-inset-top, 0px) + 10px);
    left: 10px;
    transform: translateY(-200%);
    background: #005fcc;
    color: white;
    padding: 8px 16px;
    font-size: 1rem;
    z-index: 9999;
    text-decoration: none;
    transition: transform 0.2s ease;
}

/* ACCESSIBILITY: On focus, slide into view (CSS :focus for keyboard, class for VoiceOver) */
.skip-link:focus,
.skip-link--visible {
    transform: translateY(0);
}
</style>
